import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Modale in-page réutilisable pour afficher :
 *  - type='pdf'       : visionneuse PDF en plein écran (iframe)
 *  - type='echeancier': résumé de l'échéancier de paiement
 *  - type='assurance' : résumé de l'assurance multirisque
 * Fermeture : clic sur overlay, bouton ✕, ou touche Escape.
 */
const InfoModal = ({ open, onClose, type, title, pdfUrl }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="info-modal-overlay"
    >
      <div
        className="bg-white w-full sm:max-w-3xl max-h-full sm:max-h-[90vh] sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={`info-modal-${type}`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2a4f7a] text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="font-bold text-base sm:text-lg">{title}</h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center"
            aria-label="Fermer"
            data-testid="info-modal-close"
          >✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {type === 'pdf' && pdfUrl && (
            <div className="h-full min-h-[70vh]">
              <iframe
                src={pdfUrl}
                title={title}
                className="w-full h-full min-h-[70vh] border-0"
                data-testid="info-modal-pdf-iframe"
              />
              <div className="p-3 bg-gray-50 border-t text-center">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#5BC5D4] text-sm font-semibold underline"
                  data-testid="info-modal-pdf-open-tab"
                >
                  Ouvrir dans un nouvel onglet
                </a>
              </div>
            </div>
          )}

          {type === 'echeancier' && (
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600">Votre voyage se règle en plusieurs étapes :</p>
              <div className="space-y-0">
                <div className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <div className="bg-[#79c9ea] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">1</div>
                    <div className="w-0.5 h-full bg-[#79c9ea]/30 min-h-[40px]"></div>
                  </div>
                  <div className="pb-6">
                    <h3 className="font-bold text-[#1E3A5F] text-sm">À l'inscription</h3>
                    <p className="text-xs text-gray-700 mt-1"><strong>20 % minimum</strong> du forfait (50 % si moins de 6 mois avant le départ, 100 % si moins de 45 jours, 100 % systématique pour les voyages à la journée)</p>
                    <p className="text-xs text-gray-500 mt-0.5">+ Adhésion annuelle + Assurance si souscrite</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <div className="bg-[#79c9ea] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">2</div>
                    <div className="w-0.5 h-full bg-[#79c9ea]/30 min-h-[40px]"></div>
                  </div>
                  <div className="pb-6">
                    <h3 className="font-bold text-[#1E3A5F] text-sm">6 mois avant le départ</h3>
                    <p className="text-xs text-gray-700 mt-1"><strong>50 % minimum</strong> du forfait doit être réglé</p>
                  </div>
                </div>
                <div className="flex gap-4 items-start">
                  <div className="flex flex-col items-center">
                    <div className="bg-[#79c9ea] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">3</div>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E3A5F] text-sm">45 jours avant le départ</h3>
                    <p className="text-xs text-gray-700 mt-1"><strong>Solde total</strong> du voyage doit être réglé</p>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                Difficultés ponctuelles pour respecter l'échéancier (familles nombreuses, budget serré) ? Contactez-nous, nous trouverons un arrangement.
              </div>
              <Link
                to="/informations-administratives#section-echeancier"
                onClick={() => { onClose(); setTimeout(() => window.scrollTo(0, 0), 0); }}
                className="block text-center text-[#5BC5D4] text-sm font-semibold underline"
                data-testid="info-modal-see-full"
              >
                Voir toutes les informations administratives →
              </Link>
            </div>
          )}

          {type === 'assurance' && (
            <div className="p-5 space-y-3 text-sm">
              <p className="text-gray-600">L'assurance multirisque est <strong>fortement conseillée</strong> et couvre notamment :</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0 mt-0.5">✓</span><span><strong>Annulation avant départ</strong> — maladie, accident, décès, licenciement, refus de visa, etc.</span></li>
                <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0 mt-0.5">✓</span><span><strong>Assistance rapatriement</strong> — transport médical, retour anticipé, recherche en mer/montagne</span></li>
                <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0 mt-0.5">✓</span><span><strong>Frais médicaux à l'étranger</strong> — hospitalisation, soins dentaires d'urgence</span></li>
                <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0 mt-0.5">✓</span><span><strong>Pertes et vol de bagages</strong> — remboursement sur justificatifs</span></li>
                <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0 mt-0.5">✓</span><span><strong>Interruption de voyage</strong> — remboursement des prestations non utilisées</span></li>
                <li className="flex items-start gap-2"><span className="text-[#79c9ea] font-bold flex-shrink-0 mt-0.5">✓</span><span><strong>Protection accident corporel</strong> — capital décès, incapacité permanente</span></li>
              </ul>
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs text-[#1E3A5F]">
                L'assurance doit être <strong>souscrite en même temps que le voyage</strong>. Votre interlocuteur privilégié en cas de sinistre : l'association (prévenez les accompagnateurs sur place).
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                <strong>Sans assurance, en cas d'annulation, les frais restent intégralement à votre charge.</strong>
              </div>
              <Link
                to="/informations-administratives#section-assurance-info"
                onClick={() => { onClose(); setTimeout(() => window.scrollTo(0, 0), 0); }}
                className="block text-center text-[#5BC5D4] text-sm font-semibold underline"
                data-testid="info-modal-see-full"
              >
                Voir les garanties complètes et documents →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
